/**
 * CameraShutterButton - Main capture button
 *
 * Design: Snapchat-inspired with expanding animation and progress ring
 * - 72px outer ring (3px stroke) - expands to 84px when recording
 * - 62px inner fill - shrinks to 24px red square when recording
 * - Progress ring fills as you record (0-100%)
 *
 * iOS Touch Handling:
 * - Prevents text selection on long-press
 * - Prevents context menu / callout
 * - Swallows iOS's compatibility mouse events once the button has been touched
 *   (BUG-097/098) — see the latch below.
 */
import { forwardRef, useRef, type ButtonHTMLAttributes } from 'react';

export interface CameraShutterButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  isRecording?: boolean;
  disabled?: boolean;
  /** Recording progress 0-1 (0% to 100%) */
  progress?: number;
}

const CameraShutterButton = forwardRef<HTMLButtonElement, CameraShutterButtonProps>(
  (
    {
      isRecording = false,
      disabled,
      progress = 0,
      className = '',
      onTouchStart,
      onMouseDown,
      onMouseUp,
      onMouseLeave,
      onMouseMove,
      ...props
    },
    ref
  ) => {
    // BUG-097/098: after every tap iOS replays the touch as a compatibility
    // mousedown/mouseup pair, and the shutter binds both families to the same
    // handlers — so one tap armed the shutter twice and took TWO photos, the
    // second of which the user never saw. It sat in Drafts forever as the
    // phantom "unsent" moment. The classic cure, preventDefault() on
    // touchstart, cannot work here: React 18 registers touchstart at the root
    // as a PASSIVE listener, so the call is a silent no-op. So we latch
    // instead — a device that has produced one touch is a touch device, and
    // its mouse events are never real from here on.
    const sawTouchRef = useRef(false);
    const handleTouchStart = (e: React.TouchEvent<HTMLButtonElement>) => {
      sawTouchRef.current = true;
      onTouchStart?.(e);
    };
    // A mouse-only environment (desktop dev) never sets the latch, so these
    // forward exactly as before.
    const ifRealMouse =
      <E,>(handler?: (e: E) => void) =>
      (e: E) => {
        if (sawTouchRef.current) return;
        handler?.(e);
      };

    // SVG circle math for progress ring
    const size = isRecording ? 84 : 72;
    const strokeWidth = 4;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference * (1 - progress);

    return (
      <button
        ref={ref}
        disabled={disabled}
        aria-label={isRecording ? 'Stop recording' : 'Capture'}
        onTouchStart={handleTouchStart}
        onMouseDown={ifRealMouse(onMouseDown)}
        onMouseUp={ifRealMouse(onMouseUp)}
        onMouseLeave={ifRealMouse(onMouseLeave)}
        onMouseMove={ifRealMouse(onMouseMove)}
        onContextMenu={(e) => e.preventDefault()}
        style={{
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          touchAction: 'manipulation',
        }}
        className={`
          relative flex items-center justify-center
          transition-all duration-200 ease-out
          focus:outline-none disabled:opacity-50
          active:scale-95
          select-none
          ${isRecording ? 'w-[84px] h-[84px]' : 'w-[72px] h-[72px]'}
          ${className}
        `}
        {...props}
      >
        {/* Outer ring - static border */}
        <div
          className={`
            absolute inset-0 rounded-full
            border-[3px] transition-all duration-200
            ${isRecording ? 'border-white/30' : 'border-text-inverse'}
          `}
        />

        {/* Progress ring - fills as you record */}
        {isRecording && (
          <svg
            className="absolute inset-0 -rotate-90"
            width={size}
            height={size}
          >
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="var(--color-error)"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              style={{ transition: 'stroke-dashoffset 0.1s linear' }}
            />
          </svg>
        )}

        {/* Inner fill - shrinks to square when recording */}
        <div
          className={`
            transition-all duration-200
            ${isRecording
              ? 'w-6 h-6 rounded-[4px] bg-error'
              : 'w-[62px] h-[62px] rounded-full bg-text-inverse'
            }
          `}
        />
      </button>
    );
  }
);

CameraShutterButton.displayName = 'CameraShutterButton';

export default CameraShutterButton;
